import { z } from "zod";
const IDENTIFIER_PATTERN = "^[a-z][a-z0-9_]{0,79}$";
/** A field of the queried product, or `<productSlug>.<field>` for a field of the product it joins. */
const FIELD_PATTERN = "^(?:[a-z0-9](?:[a-z0-9-]{0,118}[a-z0-9])?\\.)?[a-z][a-z0-9_]{0,79}$";
const identifier = z.string().regex(new RegExp(IDENTIFIER_PATTERN, "u"));
const fieldRef = z.string().regex(new RegExp(FIELD_PATTERN, "u"));
/** The joined product and field a `<productSlug>.<field>` reference names; undefined for the queried product's own field. */
export function twinGovernedJoinedFieldRef(ref) {
    const dot = ref.indexOf(".");
    return dot < 0 ? undefined : { productSlug: ref.slice(0, dot), field: ref.slice(dot + 1) };
}
/**
 * The model names a measure and how to read it; the runtime binds the basis the turn's contract declares.
 * A saved workflow source may pin the basis it was written against, and a changed basis refuses it.
 */
const measureSelection = z.object({
    field: identifier,
    basisSha256: z.string().regex(/^[0-9a-f]{64}$/u).optional(),
    interpretation: z.enum(["stored_source_value", "declared_measure"]),
}).strict();
const scalar = z.union([z.string().max(500), z.boolean()]);
export const twinGovernedQueryFilterSchema = z.discriminatedUnion("operator", [
    z.object({ field: fieldRef, operator: z.enum(["eq", "not_eq", "gt", "gte", "lt", "lte"]), value: scalar }).strict(),
    z.object({ field: fieldRef, operator: z.enum(["contains", "not_contains"]), value: z.string().max(500) }).strict(),
    z.object({ field: fieldRef, operator: z.enum(["in", "not_in"]), values: z.array(scalar).min(1).max(50) }).strict(),
    z.object({ field: fieldRef, operator: z.enum(["is_null", "is_not_null"]) }).strict(),
]);
export const twinGovernedQueryOrderBySchema = z.object({
    field: fieldRef,
    direction: z.enum(["asc", "desc"]),
}).strict();
const metricFunctions = ["count_rows", "count_non_null", "count_distinct", "sum", "average", "minimum", "maximum"];
export const twinGovernedQueryMetricSchema = z.object({
    alias: identifier,
    function: z.enum(metricFunctions),
    field: fieldRef.nullish(),
}).strict().superRefine((value, context) => {
    if (value.function === "count_rows" && value.field) {
        context.addIssue({ code: "custom", path: ["field"], message: "count_rows does not accept a field" });
    }
    if (value.function !== "count_rows" && !value.field) {
        context.addIssue({ code: "custom", path: ["field"], message: `${value.function} requires a field` });
    }
});
const commonQuery = {
    id: identifier,
    filters: z.array(twinGovernedQueryFilterSchema).max(16).default([]),
    orderBy: z.array(twinGovernedQueryOrderBySchema).max(4).default([]),
    limit: z.number().int().min(1).max(200).default(20),
};
const recordsQuery = z.object({
    ...commonQuery,
    kind: z.literal("records"),
    fields: z.array(fieldRef).min(1).max(20),
}).strict();
const aggregateQuery = z.object({
    ...commonQuery,
    kind: z.literal("aggregate"),
    groupBy: z.array(fieldRef).max(8).default([]),
    metrics: z.array(twinGovernedQueryMetricSchema).min(1).max(12),
}).strict();
/** A product the queried product holds a declared key to; `on` names the key's fields when it holds several. */
export const twinGovernedQueryJoinSchema = z.object({
    productSlug: z.string().trim().min(1).max(120),
    on: z.array(identifier).min(1).max(3).optional(),
}).strict();
/**
 * Keeps only the queried product's rows that have no rows, or some rows, in another product that
 * holds a declared key to it. `filters` name that product's own fields and restrict which of its
 * rows count; `on` names its key's fields when it holds several keys to the queried product.
 */
export const twinGovernedQueryRelatedRowsSchema = z.object({
    productSlug: z.string().trim().min(1).max(120),
    require: z.enum(["none", "any"]),
    on: z.array(identifier).min(1).max(3).optional(),
    filters: z.array(twinGovernedQueryFilterSchema).max(8).default([]),
}).strict();
export const twinGovernedQueryRequestSchema = z.object({
    productSlug: z.string().trim().min(1).max(120),
    join: twinGovernedQueryJoinSchema.optional(),
    relatedRows: twinGovernedQueryRelatedRowsSchema.optional(),
    measureBasis: z.array(measureSelection).max(32).default([]),
    queries: z.array(z.discriminatedUnion("kind", [recordsQuery, aggregateQuery])).min(1).max(4),
}).strict().superRefine((value, context) => {
    if (new Set(value.measureBasis.map(({ field }) => field)).size !== value.measureBasis.length) {
        context.addIssue({ code: "custom", path: ["measureBasis"], message: "Measure selections must be unique" });
    }
    if (new Set(value.queries.map((query) => query.id)).size !== value.queries.length) {
        context.addIssue({ code: "custom", path: ["queries"], message: "query IDs must be unique" });
    }
    if (value.join?.productSlug === value.productSlug) {
        context.addIssue({ code: "custom", path: ["join", "productSlug"], message: "A product cannot join itself" });
    }
    if (value.join?.on && new Set(value.join.on).size !== value.join.on.length) {
        context.addIssue({ code: "custom", path: ["join", "on"], message: "Join key fields must be unique" });
    }
    for (const ref of twinGovernedQueryFieldRefs(value.queries)) {
        const joined = twinGovernedJoinedFieldRef(ref);
        if (joined && joined.productSlug !== value.join?.productSlug) {
            context.addIssue({ code: "custom", path: ["queries"],
                message: `${ref} names a product the request does not join` });
        }
    }
    const related = value.relatedRows;
    if (related && value.join) {
        // A certificate names the queried product and one other.
        context.addIssue({ code: "custom", path: ["relatedRows"], message: "A request reads one other product: join or relatedRows" });
    }
    if (related?.productSlug === value.productSlug) {
        context.addIssue({ code: "custom", path: ["relatedRows", "productSlug"], message: "A product's related rows are another product's" });
    }
    if (related?.on && new Set(related.on).size !== related.on.length) {
        context.addIssue({ code: "custom", path: ["relatedRows", "on"], message: "Key fields must be unique" });
    }
    if (related?.filters.some(({ field }) => twinGovernedJoinedFieldRef(field))) {
        context.addIssue({ code: "custom", path: ["relatedRows", "filters"],
            message: "Related-row filters name the related product's own fields, unqualified" });
    }
});
/** Every field reference a batch makes: filters, ordering, record fields, groups and metric fields. */
export function twinGovernedQueryFieldRefs(queries) {
    return [...new Set(queries.flatMap((query) => [
            ...query.filters.map(({ field }) => field),
            ...query.orderBy.map(({ field }) => field),
            ...(query.kind === "records" ? query.fields
                : [...query.groupBy, ...query.metrics.flatMap(({ field }) => field ? [field] : [])]),
        ]))];
}
/** One advertised tool schema for every routing architecture and its catalog hash. */
export function twinGovernedProductQueryInputSchema(contractSlugs) {
    const identifierValue = { type: "string", pattern: IDENTIFIER_PATTERN };
    const field = { type: "string", pattern: FIELD_PATTERN };
    const scalarValue = { type: ["string", "boolean"] };
    const filtersOn = (field, maxItems) => ({
        type: "array", maxItems, items: { oneOf: [
                {
                    type: "object", additionalProperties: false,
                    properties: { field, operator: { type: "string", enum: ["eq", "not_eq", "gt", "gte", "lt", "lte"] }, value: scalarValue },
                    required: ["field", "operator", "value"],
                },
                {
                    type: "object", additionalProperties: false,
                    properties: { field, operator: { type: "string", enum: ["contains", "not_contains"] }, value: { type: "string", maxLength: 500 } },
                    required: ["field", "operator", "value"],
                },
                {
                    type: "object", additionalProperties: false,
                    properties: { field, operator: { type: "string", enum: ["in", "not_in"] }, values: { type: "array", minItems: 1, maxItems: 50, items: scalarValue } },
                    required: ["field", "operator", "values"],
                },
                {
                    type: "object", additionalProperties: false,
                    properties: { field, operator: { type: "string", enum: ["is_null", "is_not_null"] } },
                    required: ["field", "operator"],
                },
            ] },
    });
    const filters = filtersOn(field, 16);
    const ordering = {
        type: "array", maxItems: 4,
        items: { type: "object", additionalProperties: false,
            properties: { field, direction: { type: "string", enum: ["asc", "desc"] } },
            required: ["field", "direction"] },
    };
    const common = { filters, orderBy: ordering, limit: { type: "number", minimum: 1, maximum: 200 } };
    return {
        type: "object",
        additionalProperties: false,
        properties: {
            productSlug: { type: "string", enum: [...new Set(contractSlugs)].sort() },
            join: {
                type: "object", additionalProperties: false,
                description: "Join one product the queried product's card lists under keys. Every queried row is kept; name the joined product's fields as <productSlug>.<field>. Only its dimensions can be selected, grouped, filtered or counted.",
                properties: {
                    productSlug: { type: "string", enum: [...new Set(contractSlugs)].sort() },
                    on: { type: "array", minItems: 1, maxItems: 3, items: identifierValue,
                        description: "The queried product's key fields, only when it holds more than one key to that product." },
                },
                required: ["productSlug"],
            },
            relatedRows: {
                type: "object", additionalProperties: false,
                description: "Keep only the queried product's rows that have no rows (require: none) or some rows (require: any) in another product whose card lists a key to the queried product. Use it for absences, such as customers with no ledger lines in a year. filters name that other product's own fields and restrict which of its rows count. Not combinable with join.",
                properties: {
                    productSlug: { type: "string", enum: [...new Set(contractSlugs)].sort() },
                    require: { type: "string", enum: ["none", "any"] },
                    on: { type: "array", minItems: 1, maxItems: 3, items: identifierValue,
                        description: "The other product's key fields, only when it holds more than one key to the queried product." },
                    filters: filtersOn(identifierValue, 8),
                },
                required: ["productSlug", "require"],
            },
            measureBasis: {
                type: "array", maxItems: 32,
                description: "List every selected or filtered measure with its interpretation; the runtime binds the basis its card declares. Use stored_source_value only when the question requires the original source values; a normalized or transformed basis cannot satisfy it.",
                items: { type: "object", additionalProperties: false,
                    properties: { field: identifierValue,
                        basisSha256: { type: "string", pattern: "^[0-9a-f]{64}$",
                            description: "Leave out. A saved workflow source may carry the basis it was written against; a changed basis is refused." },
                        interpretation: { type: "string", enum: ["stored_source_value", "declared_measure"] } },
                    required: ["field", "interpretation"] },
            },
            queries: {
                type: "array", minItems: 1, maxItems: 4,
                description: "Batch all record and aggregate result shapes needed for this answer.",
                items: { oneOf: [
                        {
                            type: "object", additionalProperties: false,
                            properties: { id: identifierValue, kind: { type: "string", enum: ["records"] },
                                fields: { type: "array", minItems: 1, maxItems: 20, items: field }, ...common },
                            required: ["id", "kind", "fields"],
                        },
                        {
                            type: "object", additionalProperties: false,
                            properties: {
                                id: identifierValue, kind: { type: "string", enum: ["aggregate"] },
                                groupBy: { type: "array", maxItems: 8, items: field },
                                metrics: { type: "array", minItems: 1, maxItems: 12,
                                    items: { type: "object", additionalProperties: false,
                                        properties: { alias: identifierValue,
                                            function: { type: "string", enum: metricFunctions,
                                                description: "count_rows counts all records. count_non_null counts records with a recorded field value, including zeros; missing values do not count. Include these counts beside sums in the same grouped query when needed." },
                                            field: { type: ["string", "null"] } },
                                        required: ["alias", "function"] } },
                                ...common,
                            },
                            required: ["id", "kind", "metrics"],
                        },
                    ] },
            },
        },
        required: ["productSlug", "queries"],
    };
}
